import React from 'react';
import { ADMDirectiveHero } from '../components/BlogIllustrations';
import BlogArticle, { ArticleH2, PullQuote, WeekPlan } from '../components/BlogArticle';

const levels = [
  ['I', 'Little to no impact', 'Informational tools, analytics dashboards'],
  ['II', 'Moderate impact', 'Queue prioritization, case routing'],
  ['III', 'High impact', 'Benefit eligibility, risk assessments'],
  ['IV', 'Very high impact', 'Health, safety, liberty, or legal decisions'],
];

function BlogPostADMDirective() {
  return (
    <BlogArticle
      slug="canadas-automated-decision-making-directive"
      tags={['Public Sector', 'Compliance']}
      title={<>Canada's Automated Decision-Making Directive: What Public Sector Vendors Must Do Before <em>June 2026</em></>}
      lead="The Treasury Board's Directive on Automated Decision-Making has a hard compliance deadline approaching in June 2026. Every federal automated decision system — including those procured before the latest amendments — must be fully compliant. Here's what public sector teams and their vendor partners need to know and do right now."
      figure={<ADMDirectiveHero />}
      cta={{
        title: 'Need help getting compliant before June?',
        text: "We've helped federal departments and their vendors audit Power Platform workflows, complete Algorithmic Impact Assessments, and implement compliant human-in-the-loop architectures. Book a 30-minute session and we'll assess where you stand.",
        label: 'Book a Consultation',
      }}
    >
      <ArticleH2 id="deadline">The deadline: June 24, 2026</ArticleH2>
      <p className="blog-dropcap">
        The Treasury Board of Canada Secretariat updated the Directive on Automated Decision-Making to require that all existing automated decision systems — not just new deployments — achieve full compliance by June 24, 2026. This includes systems procured or deployed before the directive's original publication in 2019. If your organization uses Power Automate workflows, AI-driven case routing, chatbots that triage public inquiries, or any software that influences decisions affecting individuals' rights or interests, this deadline applies to you.
      </p>

      <ArticleH2 id="what-counts">What counts as an "automated decision system"?</ArticleH2>
      <p>
        The directive uses a broad definition. An automated decision system is any technology that either makes or assists in making an administrative decision about a person. This includes:
      </p>
      <ul>
        <li><strong>Fully automated systems</strong> — decisions made without human intervention (e.g., eligibility screening, automated denials)</li>
        <li><strong>Decision-support systems</strong> — tools that recommend, score, rank, or flag cases for human review (e.g., risk-scoring models, priority queues)</li>
        <li><strong>AI-powered intake and triage</strong> — chatbots or workflows that route inquiries and determine service levels</li>
      </ul>
      <p>
        If your system touches a decision that affects someone's access to a government service, benefit, or process, it likely falls under the directive.
      </p>

      <ArticleH2 id="step-1">Step 1: Complete an Algorithmic Impact Assessment</ArticleH2>
      <p>
        The Algorithmic Impact Assessment (AIA) is the cornerstone of the directive. It's a structured questionnaire that evaluates your system's potential impact on individuals and communities. The AIA classifies your system into one of four impact levels:
      </p>
      <ol className="blog-levels" aria-label="AIA impact levels" data-reveal-stagger>
        {levels.map(([lvl, name, eg]) => (
          <li className="blog-level" key={lvl}>
            <span className="blog-level__meter" aria-hidden="true"><span /><span /><span /><span /></span>
            <strong className="blog-level__name">Level {lvl} — {name}</strong>
            <span className="blog-level__eg">{eg}</span>
          </li>
        ))}
      </ol>
      <p>
        The impact level determines the requirements you must meet — from basic documentation (Level I) to independent audits, peer review, and public disclosure (Level IV). Many organizations underestimate their impact level. A chatbot that denies service or a workflow that deprioritizes certain cases can push your system into Level III or IV territory.
      </p>

      <ArticleH2 id="step-2">Step 2: Map your human-in-the-loop requirements</ArticleH2>
      <p>
        The directive mandates human oversight proportional to impact. For Level II and above, a qualified human must be able to review and override any automated decision before it takes effect. For Level III and IV, decisions must include a meaningful explanation to the affected individual and a documented recourse process.
      </p>
      <p className="blog-callout">
        In practice, this means auditing every workflow where automation touches a decision point. Ask: <em>Can a human intervene before this decision reaches the individual? Is the override mechanism documented and actually used? Are staff trained to exercise judgment rather than rubber-stamp the system's output?</em>
      </p>

      <ArticleH2 id="step-3">Step 3: Audit your existing Power Automate and AI workflows</ArticleH2>
      <p>
        Many federal departments have accumulated Power Automate flows, Power Apps, and AI models that were deployed iteratively — often without a centralized inventory. Before June, you need to:
      </p>
      <ul>
        <li><strong>Inventory every automated workflow</strong> that touches administrative decisions</li>
        <li><strong>Classify each</strong> against the AIA framework</li>
        <li><strong>Document data sources, logic, and outputs</strong> in plain language</li>
        <li><strong>Verify data quality and bias controls</strong> — the directive requires that training data and decision logic do not produce discriminatory outcomes</li>
        <li><strong>Confirm notice and transparency</strong> — affected individuals must be informed when an automated system is involved in a decision about them</li>
      </ul>

      <ArticleH2 id="step-4">Step 4: Establish ongoing governance</ArticleH2>
      <p>
        Compliance is not a one-time exercise. The directive requires ongoing monitoring, periodic reassessment, and documentation of system changes. This means:
      </p>
      <ul>
        <li>Assigning a designated owner for each automated system</li>
        <li>Scheduling regular AIA reviews (at minimum when the system changes materially)</li>
        <li>Maintaining audit logs for decisions and overrides</li>
        <li>Training staff on their oversight responsibilities</li>
        <li>Publishing AIA results for Level III and IV systems</li>
      </ul>

      <ArticleH2 id="vendors">What vendors and integrators should do now</ArticleH2>
      <p>
        If you're a technology vendor supplying automated tools to the federal government, your clients will be asking you for compliance support. Proactively prepare:
      </p>
      <ul>
        <li><strong>System documentation packages</strong> — plain-language descriptions of what the system does, what data it uses, and how decisions are made</li>
        <li><strong>Bias and fairness testing results</strong> — demonstrate that your system has been evaluated for discriminatory outcomes</li>
        <li><strong>Human override architecture</strong> — show how your system supports human review at each decision point</li>
        <li><strong>Transparency templates</strong> — pre-built notice text that departments can customize for affected individuals</li>
        <li><strong>Change management procedures</strong> — document how updates to the system trigger re-assessment</li>
      </ul>

      <ArticleH2 id="non-compliance">The cost of non-compliance</ArticleH2>
      <p>
        While the directive itself does not specify fines, non-compliance carries significant risks. Departmental audits by the Office of the Auditor General can flag non-compliant systems. Procurement processes increasingly require AIA completion as a prerequisite. And public trust — already fragile around government AI use — erodes quickly when automated systems make errors without proper oversight.
      </p>
      <PullQuote>The June 2026 deadline is not aspirational. It is a hard compliance date.</PullQuote>
      <p>
        Organizations that haven't started should begin immediately — and those already underway should verify their assessments are thorough, not superficial.
      </p>

      <ArticleH2 id="checklist">A practical 90-day checklist</ArticleH2>
      <WeekPlan
        label="90-day checklist"
        items={[
          ['Week 1-2', 'Inventory all automated decision systems across your department or product suite'],
          ['Week 3-4', 'Complete or update Algorithmic Impact Assessments for each system'],
          ['Week 5-6', 'Identify gaps in human oversight, documentation, and transparency'],
          ['Week 7-8', 'Implement remediation — add override mechanisms, update notices, document logic'],
          ['Week 9-10', 'Train staff on oversight responsibilities and recourse procedures'],
          ['Week 11-12', 'Final review, publish AIA results where required, establish ongoing monitoring'],
        ]}
      />
    </BlogArticle>
  );
}

export default BlogPostADMDirective;
